import { useEffect, useMemo, useState } from "react";
import { useProfileStore } from "../../stores/UserStore";
import { useLanguage } from "../../stores/LanguageStore";
import { updateSelf } from "../../api/UserAPI";
import {
  Group,
  LoadingOverlay,
  RingProgress,
  ThemeIcon,
  Tooltip,
} from "@mantine/core";
import { IconDeviceMobile, IconUserSearch } from "@tabler/icons-react";
import { IconLinkedin } from "../../components/Icons/IconLinkedin";
import { IconEmail } from "../../assets/icons/IconEmail";

const HeaderStats = () => {
  const lang = useLanguage((s) => s.language);
  const profile = useProfileStore((s) => s.profile);

  const [loading, setLoading] = useState(false);

  const linkedinAccounts = useMemo(() => {
    return profile?.permissions?.find(
      (x) => x.name === "maxIntegrationsLinkedin"
    );
  }, [profile]);
  const emails = useMemo(() => {
    return profile?.permissions?.find(
      (x) => x.name === "maxIntegrationsEmailSMTP"
    );
  }, [profile]);
  const enrichments = useMemo(() => {
    return profile?.permissions?.find(
      (x) => x.name === "maxEmailsGeneratedPerMonth"
    );
  }, [profile]);
  const mobilePhones = useMemo(() => {
    return profile?.permissions?.find(
      (x) => x.name === "maxMobilePhonesGeneratedPerMonth"
    );
  }, [profile]);

  useEffect(() => {
    setLoading(true);
    updateSelf(true).finally(() => setLoading(false));
  }, []);

  return (
    <Group px={"sm"} gap={"sm"} py={8} pos={"relative"} justify="space-between">
      <LoadingOverlay visible={loading} loaderProps={{ size: 24 }} />
      <Tooltip
        label={lang.header.stats.email
          .replace("#", emails?.value - emails?.remaining)
          .replace("$", emails?.value)}
      >
        <RingProgress
          label={
            <Group justify="center" ml={8} mt={-6}>
              <ThemeIcon size={"md"} variant="subtle" color="yellow">
                <IconEmail size={20} />
              </ThemeIcon>
            </Group>
          }
          sections={[
            {
              value:
                ((emails?.value - emails?.remaining) / emails?.value) * 100,
              color: emails?.remaining <= 0 ? "red" : "green",
            },
          ]}
          thickness={6}
          roundCaps
          size={64}
        />
      </Tooltip>

      <Tooltip
        label={lang.header.stats.linkedin
          .replace("#", linkedinAccounts?.value - linkedinAccounts?.remaining)
          .replace("$", linkedinAccounts?.value)}
      >
        <RingProgress
          label={
            <Group justify="center" ml={8} mt={-6}>
              <ThemeIcon size={"md"} variant="subtle">
                <IconLinkedin size={20} />
              </ThemeIcon>
            </Group>
          }
          sections={[
            {
              value:
                ((linkedinAccounts?.value - linkedinAccounts?.remaining) /
                  linkedinAccounts?.value) *
                100,
              color: linkedinAccounts?.remaining <= 0 ? "red" : "green",
            },
          ]}
          thickness={6}
          roundCaps
          size={64}
        />
      </Tooltip>

      <Tooltip
        label={lang.header.stats.enrich
          .replace("#", enrichments?.value - enrichments?.remaining)
          .replace("$", enrichments?.value)}
      >
        <RingProgress
          label={
            <Group justify="center" ml={8} mt={-6}>
              <ThemeIcon size={"md"} variant="subtle">
                <IconUserSearch size={20} />
              </ThemeIcon>
            </Group>
          }
          sections={[
            {
              value:
                ((enrichments?.value - enrichments?.remaining) /
                  enrichments?.value) *
                100,
              color: enrichments?.remaining <= 0 ? "red" : "green",
            },
          ]}
          thickness={6}
          roundCaps
          size={64}
        />
      </Tooltip>

      <Tooltip
        label={lang.header.stats.mobile
          .replace("#", mobilePhones?.value - mobilePhones?.remaining)
          .replace("$", mobilePhones?.value)}
      >
        <RingProgress
          label={
            <Group justify="center" ml={8} mt={-6}>
              <ThemeIcon size={"md"} variant="subtle">
                <IconDeviceMobile size={20} />
              </ThemeIcon>
            </Group>
          }
          sections={[
            {
              value:
                ((mobilePhones?.value - mobilePhones?.remaining) /
                  mobilePhones?.value) *
                100,
              color: mobilePhones?.remaining <= 0 ? "red" : "green",
            },
          ]}
          thickness={6}
          roundCaps
          size={64}
        />
      </Tooltip>
    </Group>
  );
};

export default HeaderStats;
